import {PriorityIconWithTitleProp} from "../../components/props";
import Avatar from "../../components/Avatar";
import {
  OtherTag,
  ProjectTag,
  enrichCardProjectTags,
  rawEnrichCardProjectTags,
} from "../../components/Markdown/CardTags";
import {useJustChanged} from "../../components/Highlight";
import {useCardHoverHandlers, usePropHover} from "../../components/Card/useHoverShortcuts";
import {useSpring, animated} from "react-spring";
import {springConfigs, cx, uiStyles} from "@cdx/common";
import {cardDetailFrameStyles as styles} from "./unified-card-container.css";
import miniCardStyles from "../../components/Card/card.css";
import {ChildCardInfo, MaybeChildcardOverlay} from "../../components/Card/CardBottom";
import DependencyArea, {BacklinkArea} from "../dependencies/DependencyArea";
import {cardHasDeps} from "../dependencies/dependency-utils";
import {dependencyStyles} from "../dependencies/DependencyArea.css";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {Box, Col, css, DSIconDoc, DSIconEffort, Row} from "@cdx/ds";
import {forwardRef} from "react";
import {SharedPropChangeOverlayForChild} from "../../components/PropEdit/shared";
import Beast, {getBeastLevel} from "../../components/Card/Beast";
import AddedDuringSprint, {getIsNewUntil} from "../../components/Card/AddedDuringSprint";

const useHighlight = ({card, prop}) => {
  const props = useSpring({
    opacity: 1,
    transform: "scale(1)",
    config: springConfigs.quick,
  });
  useJustChanged(
    () => {
      props.opacity.update({to: 0.75}).update({to: 1, delay: 200}).start();
      props.transform.update({to: "scale(1.15)"}).update({to: "scale(1)", delay: 200}).start();
    },
    {model: card, prop}
  );
  return props;
};

const PropChangeOverlay = ({
  card,
  onChange,
  children,
  availableShortcuts,
  type,
  cardContainerKey,
  canModify,
}) => {
  if (!canModify || (!card.id && type !== "BIG_DEFAULT_CARD")) return children;
  return (
    <SharedPropChangeOverlayForChild
      type={type}
      card={card}
      onChange={onChange}
      availableShortcuts={availableShortcuts}
      children={children}
      cardContainerKey={cardContainerKey}
    />
  );
};

const BeastArea = ({root, card, status}) => {
  const beastLevel = getBeastLevel(root.account, card);
  const isNewUntil = getIsNewUntil(root.account, card);
  return beastLevel > 0 ? (
    <Beast level={beastLevel} size="lg" isDone={status === "done"} isNewUntil={isNewUntil} />
  ) : isNewUntil ? (
    <AddedDuringSprint size="lg" isDone={status === "done"} isNewUntil={isNewUntil} />
  ) : null;
};

const Bottom = ({
  card,
  status,
  childCardInfo,
  type,
  onChildCardBarClick,
  onChange,
  root,
  canModify,
}) => {
  const handlers = useCardHoverHandlers({type});
  const effortHandlers = usePropHover("effort");
  const prioHandlers = usePropHover("priority");

  const effortStyle = useHighlight({card, prop: "effort"});
  const assigneeStyle = useHighlight({card, prop: "assignee"});
  const priorityStyle = useHighlight({card, prop: "priority"});

  const wordCount = card.meta?.content?.wordCount ?? null;

  const getContainerClass = () => {
    const base = css(
      {sp: "8px", display: "flex", flexDir: "column"},
      styles.bottomContainer,
      card.visibility === "archived" && styles.bottomContainerArchived
    );
    if (childCardInfo) {
      if (status === "hero") {
        return cx(base, styles.shinyHero);
      } else if (status === "doc") {
        return cx(base, styles.shinyDoc);
      } else {
        return cx(base, card.visibility !== "archived" && styles.shinyStatus);
      }
    } else if (status === "hero") {
      return cx(base, styles.shinyHero);
    }
    return base;
  };

  const getEffortLabel = () => {
    const effort = childCardInfo ? childCardInfo.effort : card.$meta.get("effort", null);
    return effort === null ? "-" : effort;
  };

  return (
    <footer className={getContainerClass()} {...handlers}>
      <Row>
        <animated.div
          style={{flex: "50px 1", ...effortStyle}}
          {...(childCardInfo || status === "doc" ? null : effortHandlers)}
        >
          {status === "doc" ? (
            <Row sp="4px" align="center">
              <DSIconDoc
                size={24}
                className={cx(miniCardStyles.docIcon.base, miniCardStyles.docIcon.big)}
              />
              <Box relative bold top="2px" size={10} textTransform="uppercase">
                Doc Card
              </Box>
            </Row>
          ) : (
            <PropChangeOverlay
              canModify={canModify}
              type={type}
              card={card}
              onChange={onChange}
              availableShortcuts={[
                "effort",
                ...(card.milestone ? [] : ["milestone"]),
                ...(card.sprint ? [] : ["sprint"]),
              ]}
              cardContainerKey="openCard-effort"
            >
              <Row sp="4px" align="center">
                <Box bold size={10} textTransform="uppercase">
                  Effort
                </Box>
                <DSIconEffort className={styles.effortIcon} />
                <Box bold>{getEffortLabel()}</Box>
              </Row>
            </PropChangeOverlay>
          )}
        </animated.div>
        {card.assignee ? (
          <PropChangeOverlay
            canModify={canModify}
            type={type}
            card={card}
            onChange={onChange}
            availableShortcuts={["owner"]}
            cardContainerKey="openCard-owner"
          >
            <animated.div className={styles.assigneeContainer} style={assigneeStyle}>
              <Avatar user={card.assignee} size={50} />
            </animated.div>
          </PropChangeOverlay>
        ) : (
          <PropChangeOverlay
            canModify={canModify}
            type={type}
            card={card}
            onChange={onChange}
            availableShortcuts={["owner"]}
            cardContainerKey="openCard-owner"
          >
            <div className={styles.assigneeFallback} data-cdx-clickable />
          </PropChangeOverlay>
        )}
        <Row styleChild justify="end" bold>
          <animated.div
            style={{flex: "50px 1", ...priorityStyle}}
            {...(status === "doc" ? null : prioHandlers)}
          >
            {status === "doc" ? (
              wordCount > 0 && (
                <Row align="baseline" sp="8px">
                  <Box size={10} textTransform="uppercase">
                    Words
                  </Box>
                  <Box size={16}>{card.meta.content?.wordCount || 0}</Box>
                </Row>
              )
            ) : (
              <>
                <PropChangeOverlay
                  canModify={canModify}
                  type={type}
                  card={card}
                  onChange={onChange}
                  availableShortcuts={["priority"]}
                  cardContainerKey="openCard-priority"
                >
                  <Row sp="8px" align="center">
                    <Box size={10} textTransform="uppercase">
                      Priority
                    </Box>
                    <PriorityIconWithTitleProp size={24} priority={card.priority} root={root} />
                  </Row>
                </PropChangeOverlay>
              </>
            )}
          </animated.div>
        </Row>
      </Row>
      {childCardInfo && (
        <MaybeChildcardOverlay
          cardId={card.id}
          overlayPlacement="right"
          childCardInfo={childCardInfo}
        >
          <ChildCardInfo isBig info={childCardInfo} onClick={onChildCardBarClick} />
        </MaybeChildcardOverlay>
      )}
    </footer>
  );
};

/**
 * @type: any
 */
export const CardDetailFrameAboveBottom = ({
  isWorkflowItem,
  card,
  project,
  allowDeps,
  backlinkCards,
  root,
  canModify,
}) => {
  const enrichedTags = isWorkflowItem
    ? rawEnrichCardProjectTags({masterTags: card.masterTags, project})
    : enrichCardProjectTags(card);

  const hasDeps = allowDeps && cardHasDeps(card);
  const hasBacklinks = backlinkCards && backlinkCards.length > 0;
  const hasTags = enrichedTags.length > 0;
  const rightComps = [];
  if (allowDeps && (isWorkflowItem || card.deck)) {
    rightComps.push(
      <DependencyArea
        key="dep"
        isWorkflowItem={isWorkflowItem}
        card={card}
        root={root}
        className={cx(!hasDeps && dependencyStyles.counter.hasNoDeps)}
        canModify={canModify}
      />
    );
  }
  if (hasBacklinks) {
    rightComps.push(<BacklinkArea key="backlinks" backlinkCards={backlinkCards} root={root} />);
  }
  if (!hasTags && rightComps.length === 0) return null;
  return (
    <Row
      className={cx(styles.bottomArea, uiStyles.hideContainer)}
      px="16px"
      pb={card.assignee ? "32px" : "16px"}
    >
      {hasTags && (
        <Row wrap sp="8px" pt="12px">
          {enrichedTags.map((tagObj) => (
            <div key={tagObj.tag}>
              {tagObj.isDeprecated ? (
                <OtherTag tag={tagObj.tag} />
              ) : (
                <ProjectTag tagObject={tagObj} />
              )}
            </div>
          ))}
        </Row>
      )}
      {rightComps.length > 0 && (
        <Row sp="12px" pt="8px" ml="auto">
          {rightComps}
        </Row>
      )}
    </Row>
  );
};

/**
 * @type: any
 */
const CardDetailFrame = forwardRef(
  (
    {
      card,
      children,
      status,
      childCardInfo,
      type,
      onChildCardBarClick,
      root,
      onCardChange,
      canModify,
      withCoverImage,
      transparentBg,
    },
    passedRef
  ) => {
    return (
      <animated.div
        ref={passedRef}
        className={css(
          {
            display: "flex",
            flexDir: "column",
            position: "relative",
          },
          cardStatusVariants[status],
          styles.container.base,
          styles.container.type[withCoverImage ? "withCoverImage" : "default"]
        )}
      >
        <Col
          flex="auto"
          minHeight={0}
          colorTheme="white"
          elevation={200}
          rounded="openCard"
          overflow="hidden"
        >
          <Col
            flex="auto"
            colorTheme="white"
            roundedTop="card"
            minHeight={0}
            bg={transparentBg ? undefined : "foreground"}
            color="primary"
          >
            {children}
          </Col>
          <Bottom
            card={card}
            status={status}
            childCardInfo={childCardInfo}
            type={type}
            onChildCardBarClick={onChildCardBarClick}
            onChange={onCardChange}
            root={root}
            canModify={canModify}
          />
        </Col>
        <BeastArea root={root} card={card} status={status} />
      </animated.div>
    );
  }
);

export default CardDetailFrame;
